import type { Repeat, Weekday } from "../types";

/** A schedule's message in the form: a workflow with raw arguments, or a prompt. */
export type ScheduleMessage = { kind: "workflow"; workflow: string; args: string } | { kind: "prompt"; prompt: string };

/** The text a schedule sends. A workflow goes out as a person would type it (ADR-0001). */
export function composeText(message: ScheduleMessage): string {
	if (message.kind === "prompt") return message.prompt;
	const args = message.args.trim();
	return args ? `/${message.workflow} ${args}` : `/${message.workflow}`;
}

/** Reads saved text back into the form: a workflow when it starts with one the profile has, else a prompt. */
export function decomposeText(text: string, workflows: string[]): ScheduleMessage {
	const match = /^\/(\S+)(?:\s+([\s\S]*))?$/.exec(text);
	if (match && workflows.includes(match[1]!)) return { kind: "workflow", workflow: match[1]!, args: match[2] ?? "" };
	return { kind: "prompt", prompt: text };
}

/** Monday first, as the form lists them. */
export const WEEKDAY_NAMES: ReadonlyArray<[Weekday, string]> = [
	["mon", "周一"],
	["tue", "周二"],
	["wed", "周三"],
	["thu", "周四"],
	["fri", "周五"],
	["sat", "周六"],
	["sun", "周日"],
];

/** 每天 08:00 · 每周一 08:00 · 每月 15 号 08:00 · 每月最后一天 08:00. */
export function describeRepeat(repeat: Repeat | null): string {
	if (!repeat) return "旧规则，请重新设置";
	switch (repeat.every) {
		case "day":
			return `每天 ${repeat.at}`;
		case "week":
			return `每${WEEKDAY_NAMES.find(([w]) => w === repeat.weekday)![1]} ${repeat.at}`;
		case "month":
			return repeat.day === "last" ? `每月最后一天 ${repeat.at}` : `每月 ${repeat.day} 号 ${repeat.at}`;
	}
}

/** The repeat after picking a frequency: the time stays, a new day starts on 周一 or 1 号. */
export function withEvery(repeat: Repeat, every: Repeat["every"]): Repeat {
	if (every === repeat.every) return repeat;
	if (every === "day") return { every, at: repeat.at };
	if (every === "week") return { every, at: repeat.at, weekday: "mon" };
	return { every, at: repeat.at, day: 1 };
}

/** An active schedule with no device to push to: its results and its failures would reach nobody. */
export function needsDeviceWarning(schedules: ReadonlyArray<{ paused: boolean }>, pushDevices: number): boolean {
	return pushDevices === 0 && schedules.some((s) => !s.paused);
}
